import { Injectable } from '@angular/core';

import { GeocodeService } from '../services/geocode.service';
import { ISearchResult, ISearchResultType } from '../types';

import { Api } from './api';

@Injectable({
    providedIn: 'root'
})

export class SearchApi {
    constructor(private readonly api: Api, private readonly geocodeService: GeocodeService) { }

    getSearchResultTypes(excludeTypes: string[] = null): ISearchResultType[] {
        return [
            { name: 'feature', pluralDisplayName: 'Features' },
            { name: 'layer', pluralDisplayName: 'Layers' },
            { name: 'map', pluralDisplayName: 'Maps' }
        ].filter(type => (!excludeTypes || !excludeTypes.includes(type.name)));
    }

    async getAoiAssetIdOptions(columnName: string, options: string) {
        return await this.api.http2.graphqlRequest(`
            query GetAoiAssetIdOptions($columnName: [String]) {
                aoiAssetIdOptions(columnName: $columnName) {
                    ${options}
                }
            }
        `, { columnName }, 'GET', '');
    }

    async getAssetIds(query: string, stringIds: string) {
        return await this.api.http2.graphqlRequest(`
                query GetAssets($stringIds: String) {
                    ${query}
                }
            `, { stringIds }, 'GET', '');
    }

    async getAddressResults(text: string, maxSuggestions: number = 5) {
        const suggestResult = await this.geocodeService.suggest(text, maxSuggestions).toPromise();
        const searchResults: ISearchResult[] = suggestResult.suggestions.map((x) => {
            return {
                name: x.text,
                primaryText: x.text,
                magicKey: x.magicKey,
                type: 'address',
                layerTitle: 'Address'
            };
        });
        return searchResults;
    }

    async getMapcoreResults(text: string, userId: number = 0, type: string = '', first: number = 10, excludeTypes: string[] = null, applicationId: number = 0) {
        if (!userId) {
            userId = 0;
        }
        const nameSearch = `%${text}%`;
        const queryText = this.getQueryTextBasedOnType(type, userId > 0, excludeTypes);
        const dataResult = await this.api.http2.graphqlRequest(queryText,
            { queryText, text, nameSearch, first, userId , applicationId}, 'GET');
        if (dataResult.maps && dataResult.users && dataResult.users.length > 0) {
            dataResult.maps = dataResult.users[0].maps.concat(dataResult.maps);
        }
        const results: { [key: string]: ISearchResult[] } = {};
        const resultTypes = this.getSearchResultTypes(excludeTypes);
        for (const resultType of resultTypes) {
            const pluralType = resultType.pluralDisplayName.toLowerCase();
            const typeResults = dataResult[pluralType];
            this.standardizeDisplayFields(typeResults, resultType.name);
            results[resultType.name] = typeResults;
        }
        return results;
    }

    async getResults(text: string, userId: number = 0, type: string = '', first: number = 10, excludeTypes: string[] = null, applicationId: number = 0) {
        const results = await this.getMapcoreResults(text, userId, type, first, excludeTypes, applicationId);
        if (type === '' || type === 'address') {
            const addressResults = await this.getAddressResults(text);
            const featureKey = 'feature';
            if (addressResults && addressResults.length) {
                results[featureKey] = results[featureKey].concat(addressResults);
            }
        }
        return results;
    }

    async standardizeDisplayFields(items: Array<ISearchResult>, type: string) {
        for (const item of items) {
            item.type = type;
            if (type !== 'feature') {
                item.primaryText = item.name;
                const id = item.id;
                const displayType = type.toUpperCase();
                item.secondaryText = `${displayType}: ${id}`;
            }
        }
    }

    getQueryTextBasedOnType(type: string, includeUserMaps: boolean = false, excludeTypes: string[] = null) {
        const queries: string[] = [];
        const params: string[] = [];
        let includeTextSearch: boolean;
        let includeNameSearch: boolean;
        if ((!type || type === 'feature') && (!excludeTypes || !excludeTypes.includes('feature'))) {
            queries.push(`
            features(search: $text, applicationId: $applicationId, first: $first, orderBy:["searchPriority"]){
                primaryText,secondaryText,tertiaryText,layerTitle,layerId,objectId
            }`);
            includeTextSearch = true;
        }
        if ((!type || type === 'layer') && (!excludeTypes || !excludeTypes.includes('layer'))) {
            queries.push(`
            layers(search: $nameSearch, isSystem: true, first: $first, applicationId: $applicationId, orderBy:["searchPriority"]){
                id, name, description
            }`);
            includeNameSearch = true;
        }
        if ((!type || type === 'map') && (!excludeTypes || !excludeTypes.includes('map'))) {
            queries.push(`
            maps(search: $nameSearch, isSystem: true, first: $first){
            id, name, description
            }`);
            if (includeUserMaps) {
                queries.push(`
                users(id: $userId) {maps(search: $nameSearch, first: $first){
                id, name
                }}`);
                params.push('$userId: [Int]');
            }
            includeNameSearch = true;
        }

        params.push('$first: [Int]');
        if (includeTextSearch) {
            params.push('$text: [String]');
        }
        if (includeNameSearch) {
            params.push('$nameSearch: [String]');
        }
        params.push('$applicationId: Int');
        const paramText = params.join(',');
        const queryText = queries.join(',');
        const fullQuery = `query Search(${paramText}) {
            ${queryText}
        }`;
        return fullQuery;
    }
}
